import type { ContentBlock } from '@/lib/blogContentBlocks';
import { BlogContentBlocks } from '@/components/blog/BlogContentBlocks';
import { CmsSchema, useCmsFields } from '@/cms/content';
import type { CmsField } from '@workspace/url-source/cms';

/**
 * Only text nodes in the trusted, compiled legal markup become editable.
 * Links, classes, structure and the interactive cookie controls stay in code.
 * CMS values are assigned through textContent; no authored HTML is accepted.
 */
export function CmsLegalBlocks({ blocks, articleId, title }: {
  blocks: ContentBlock[];
  articleId: string;
  title: string;
}) {
  const fields: CmsField[] = [];
  const prepared = blocks.map((block, blockIndex) => {
    const document = new DOMParser().parseFromString(block.html, 'text/html');
    const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
    const nodes: { node: Node; key: string }[] = [];
    let node: Node | null;
    let textIndex = 0;
    while ((node = walker.nextNode())) {
      if (!node.textContent?.trim() || node.parentElement?.closest('script,style')) continue;
      const key = `legal.${articleId}.block.${blockIndex}.text.${textIndex++}`;
      fields.push({ key, label: `Fragment ${blockIndex + 1}.${textIndex}`, section: title, kind: 'textarea', defaultValue: node.textContent, required: true });
      nodes.push({ node, key });
    }
    return { block, document, nodes };
  });
  const cms = useCmsFields(fields);
  const publishedBlocks = prepared.map(({ block, document, nodes }) => {
    for (const { node, key } of nodes) {
      const span = document.createElement('span');
      span.setAttribute('data-cms-text', key);
      span.textContent = cms.value(key);
      node.parentNode?.replaceChild(span, node);
    }
    return { ...block, html: document.body.innerHTML };
  });
  return <><CmsSchema fields={cms.fields} /><BlogContentBlocks blocks={publishedBlocks} /></>;
}
